import React, { ClassicElement, ComponentClass, useEffect, useState } from 'react';
import { Card, Col, Row, Button, Form, Collapse, Divider, Breadcrumb, Checkbox, notification, Alert } from 'antd';
import { useHistory } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';

import PortalLoading from 'components/PortalLoading';
import {
    AddressProps,
    Price,
    SquarePaymentForm,
    FormErrorParser,
    ThreeJSThumbnail,
    ErrorRender,
} from '@partsbadger/library';
import { SQUARE_APP_ID, SQUARE_ENVIRONMENT_URL, SQUARE_LOCATION_ID, post } from '@partsbadger/utils';

import QuickTurnPartStore from 'stores/QuickTurnPartStore';
import UserStore from 'stores/UserStore';
import { trackLuckyOrangeEvent } from '@/utils/luckyOrange';
import { trackOrderCreated } from '@/utils/gtm';

import { observer } from 'mobx-react-lite';

import Menu from 'components/QuickTurn/Menu';

import { Link } from 'react-router-dom';
import { EditOutlined, CreditCardOutlined } from '@ant-design/icons';

import AddressFormSimple from '../AddressFormSimple';

const { Panel } = Collapse;

import { creditCardErrorRender } from '@partsbadger/library';

const Checkout: React.FC = () => {
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [buttonLoading, setButtonLoading] = useState(false);

    const [savedBillingAddress, setSavedBillingAddress] = useState<AddressProps>();
    const [savedShippingAddress, setSavedShippingAddress] = useState<AddressProps>();
    const [editShippingAddress, setEditShippingAddress] = useState(false);

    const [activeKey, setActiveKey] = useState<string | string[]>('2');

    // Use this code later, when implimenting guest checkouts
    // const [customerInfoFormCompleted, setCustomerInfoFormCompleted] = useState<boolean>(
    //     UserStore.isAuthenticated ? true : false
    // );

    const [billingInfoFormCompleted, setBillingInfoFormCompleted] = useState<boolean>(false);
    const [shippingInfoFormCompleted, setShippingInfoFormCompleted] = useState<boolean>(false);

    // Use this code later, when implimenting guest checkouts
    // const [emailExists, setEmailExists] = useState<boolean>(false);

    const [shippingSameAsBilling, setShippingSameAsBilling] = useState<boolean>(false);

    const buttonReference = React.useRef<HTMLButtonElement>(null);

    const { orderData } = QuickTurnPartStore;

    const [form] = Form.useForm();
    const [customerInfoForm] = Form.useForm();
    const [billingInfoForm] = Form.useForm();
    const [shippingInfoForm] = Form.useForm();

    // Use this function later, when implimenting geust checkouts
    // async function checkIfEmailExists(value: string) {
    //     try {
    //         const data = await post(`/verify-email/`, {
    //             email: value,
    //         });

    //         setEmailExists(data?.is_registered);

    //         return data;
    //     } catch (err) {
    //         message.error(`Error: ${err}`);

    //         return err;
    //     }
    // }

    // async function onClickSaveCustomerInfoHandler(values: any) {
    //     await QuickTurnPartStore.updateSalesOrder({
    //         email: values.email,
    //     });

    //     QuickTurnPartStore.getSalesOrderById(QuickTurnPartStore?.orderToken);

    //     setActiveKey('2');
    // }

    const openNotification = (message: string) => {
        notification.info({
            message: 'Important Message',
            description: `${message}`,
            duration: 10,
            placement: 'topRight',
        });
    };

    async function onClickSaveBillingInfoHandler(values: any) {
        await QuickTurnPartStore.updateSalesOrder(QuickTurnPartStore?.orderToken, {
            email: UserStore?.contact?.email,
            billing_name: values.billing_name,
            billing_phone: values.billing_phone,
            billing_street: values.billing_street,
            billing_city: values.billing_city,
            billing_state: values.billing_state?.value,
            billing_country: 'US',
            billing_zip_code: values.billing_zip_code,
        });

        QuickTurnPartStore.getSalesOrderById(QuickTurnPartStore?.orderToken);

        if (billingInfoFormCompleted) {
            setActiveKey('3');

            if (shippingInfoFormCompleted) {
                setActiveKey('4');
            }
        }
    }

    async function onClickSaveShippingInfoHandler(values: any) {
        await QuickTurnPartStore.updateSalesOrder(QuickTurnPartStore?.orderToken, {
            shipping_name: values.shipping_name,
            shipping_phone: values?.shipping_phone ? values.shipping_phone : null,
            shipping_street: values.shipping_street,
            shipping_city: values.shipping_city,
            shipping_state: values.shipping_state?.value ? values.shipping_state?.value : values.shipping_state,
            shipping_country: 'US',
            shipping_zip_code: values.shipping_zip_code,
        });

        QuickTurnPartStore.getSalesOrderById(QuickTurnPartStore?.orderToken);

        setActiveKey('4');
    }

    const handleSubmit = async (cardToken: string) => {
        setButtonLoading(true);

        const isPortalEnabled = await QuickTurnPartStore.getCompanyParams('PortalEnabledParam');

        if (!isPortalEnabled) {
            history.push('/quick-turn/cart');

            return true;
        }

        const payload = {
            credit_card_token: cardToken,
            terms_and_conditions_accepted: form.getFieldValue('terms-and-conditions'),
        };

        const redirect = () => {
            history.push('/quick-turn/cart/checkout/success');
        };

        // sendToReview clears orderToken before it resolves, so read it while it is still set.
        const orderToken = QuickTurnPartStore.orderToken;

        if (form.getFieldValue('terms-and-conditions') === true) {
            await QuickTurnPartStore.sendToReview(payload)
                .then(() => {
                    trackOrderCreated({
                        order_id: orderToken,
                        payment_method: 'credit_card',
                        source: 'quick_turn_checkout',
                    });
                    setButtonLoading(false);
                    redirect();
                })
                .catch(async err => {
                    trackLuckyOrangeEvent('Payment Failed', {
                        order_id: orderToken ?? undefined,
                        stage: 'quick_turn_checkout',
                        page: window.location.pathname,
                    });
                    if (err?.response?.status === 403) {
                        notification.error({
                            message: 'Forbidden',
                            description: err?.response?.data?.error,
                        });
                        history.push('/quick-turn/cart');
                    }

                    setButtonLoading(false);

                    creditCardErrorRender({ error: err, store: QuickTurnPartStore, showNotification: false });
                });
        }
    };

    async function getOrderData() {
        if (!QuickTurnPartStore?.orderData && QuickTurnPartStore?.orderToken) {
            await QuickTurnPartStore.getSalesOrderById(QuickTurnPartStore?.orderToken);
        }

        // Clean the observable property
        QuickTurnPartStore.setCreditCardError([]);

        setLoading(false);
    }

    useEffect(() => {
        if (!UserStore.isAuthenticated) {
            history.push('/quick-turn/design');
        }

        QuickTurnPartStore.getCompanyParams('PortalEnabledParam');

        getOrderData();
    }, []);

    if (!QuickTurnPartStore.isPortalEnabled) {
        history.goBack();
    }

    useEffect(() => {
        if (!loading && !orderData) {
            history.push('/quick-turn/design');
        }

        if (orderData?.coupon_was_removed) {
            openNotification('The coupon you entered is no longer valid, and has been removed from your cart. ');
        }

        // Use this code later, when implimenting guest checkouts
        // if (orderData?.email) {
        //     setCustomerInfoFormCompleted(true);
        // }

        if (
            orderData?.billing_name &&
            orderData?.billing_phone &&
            orderData?.billing_street &&
            orderData?.billing_city &&
            orderData?.billing_state &&
            orderData?.billing_zip_code &&
            orderData?.billing_country
        ) {
            setBillingInfoFormCompleted(true);
        }

        if (
            orderData?.shipping_name &&
            orderData?.shipping_street &&
            orderData?.shipping_city &&
            orderData?.shipping_state &&
            orderData?.shipping_zip_code &&
            orderData?.shipping_country
        ) {
            setShippingInfoFormCompleted(true);
        }

        customerInfoForm.setFieldsValue({
            email: orderData?.email ? orderData?.email : null,
        });

        billingInfoForm.setFieldsValue({
            billing_name: orderData?.billing_name ? orderData?.billing_name : null,
            billing_phone: orderData?.billing_phone ? orderData?.billing_phone : null,
            billing_street: orderData?.billing_street ? orderData?.billing_street : null,
            billing_city: orderData?.billing_city ? orderData?.billing_city : null,
            billing_state: orderData?.billing_state ? orderData?.billing_state : null,
            billing_zip_code: orderData?.billing_zip_code ? orderData?.billing_zip_code : null,
            billing_country: orderData?.billing_country ? orderData?.billing_country : null,
        });

        shippingInfoForm.setFieldsValue({
            shipping_name: orderData?.shipping_name ? orderData?.shipping_name : null,
            shipping_phone: orderData?.shipping_phone ? orderData?.shipping_phone : null,
            shipping_street: orderData?.shipping_street ? orderData?.shipping_street : null,
            shipping_city: orderData?.shipping_city ? orderData?.shipping_city : null,
            shipping_state: orderData?.shipping_state ? orderData?.shipping_state : null,
            shipping_zip_code: orderData?.shipping_zip_code ? orderData?.shipping_zip_code : null,
            shipping_country: orderData?.shipping_country ? orderData?.shipping_country : null,
        });
    }, [orderData]);

    useEffect(() => {
        if (billingInfoFormCompleted) {
            setActiveKey('3');

            if (shippingInfoFormCompleted) {
                setActiveKey('4');
            }
        }
    }, [billingInfoFormCompleted, shippingInfoFormCompleted]);

    return (
        <>
            <Menu title="Checkout" />

            <div className="container quick-turn-checkout-wrapper">
                {!orderData || loading ? (
                    <PortalLoading />
                ) : (
                    <>
                        <Breadcrumb>
                            <Breadcrumb.Item>
                                <Link to="/quick-turn/design">QuickTurn</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <Link to="/quick-turn/cart">Cart</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>Checkout</Breadcrumb.Item>
                        </Breadcrumb>

                        <Row gutter={[12, 0]}>
                            <Col lg={12} span={24}>
                                <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
                                    <div>
                                        <Collapse
                                            activeKey={activeKey}
                                            onChange={() => null}
                                            bordered={false}
                                            accordion={true}
                                            ghost={true}
                                        >
                                            {/* Use this code later, when implimenting geust checkouts */}
                                            {/*  <>
                                                {!UserStore.isAuthenticated ? (
                                                    <Panel
                                                        header={<span className="px-1">Customer Information</span>}
                                                        key="1"
                                                        showArrow={false}
                                                        extra={
                                                            activeKey !== '1'
                                                                ? [
                                                                      <>
                                                                          <Button
                                                                              type="link"
                                                                              className="ant-btn-xs h-auto rounded-pill m-0 p-0"
                                                                              onClick={() => setActiveKey('1')}
                                                                          >
                                                                              <EditOutlined />
                                                                              Change
                                                                          </Button>
                                                                      </>,
                                                                  ]
                                                                : null
                                                        }
                                                    >
                                                        <>
                                                            <Form
                                                                className={'customer-information-form'}
                                                                layout="vertical"
                                                                form={customerInfoForm}
                                                                scrollToFirstError={true}
                                                                onFinish={onClickSaveCustomerInfoHandler}
                                                                // onFieldsChange={handleCustomerInfoFormChange}
                                                            >
                                                                <Row gutter={[8, 0]} className="m-0 mb-3">
                                                                    <Col span={24}>
                                                                        <Form.Item
                                                                            name="email"
                                                                            label="Email"
                                                                            rules={[
                                                                                {
                                                                                    required: true,
                                                                                    message: 'Please enter your email',
                                                                                },
                                                                            ]}
                                                                            // hasFeedback={true}
                                                                            help={
                                                                                emailExists ? (
                                                                                    <>
                                                                                        <span>
                                                                                            An account already exists.
                                                                                        </span>{' '}
                                                                                        <Link
                                                                                            to={
                                                                                                '/login?next=/quick-turn/cart/checkout'
                                                                                            }
                                                                                        >
                                                                                            Please login here
                                                                                        </Link>
                                                                                    </>
                                                                                ) : null
                                                                            }
                                                                        >
                                                                            <Input
                                                                                onBlur={e =>
                                                                                    checkIfEmailExists(e.target.value)
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                    </Col>

                                                                    <Col span={24}>
                                                                        <Button
                                                                            type="primary"
                                                                            size="small"
                                                                            block
                                                                            htmlType="submit"
                                                                            className={emailExists ? 'mt-3' : ''}
                                                                            disabled={emailExists}
                                                                        >
                                                                            Next: Billing Information
                                                                        </Button>
                                                                    </Col>
                                                                </Row>
                                                            </Form>
                                                        </>
                                                    </Panel>
                                                ) : null}

                                                {!UserStore.isAuthenticated && activeKey !== '1' ? (
                                                    <div className="container">
                                                        <Row>
                                                            <Col span={24}>
                                                                <div className="d-flex flex-column">
                                                                    <span className="font-size-base font-weight-semibold">
                                                                        Email
                                                                    </span>
                                                                    <span className="font-size-base">
                                                                        {orderData?.email}
                                                                    </span>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                ) : null}
                                            </>*/}
                                            <Panel
                                                header={<span className="px-1">Billing Information</span>}
                                                key="2"
                                                showArrow={false}
                                                // style={{ borderTop: '1px solid #e0e0e0' }}
                                                extra={
                                                    activeKey !== '2' && billingInfoFormCompleted
                                                        ? [
                                                              <>
                                                                  <Button
                                                                      type="link"
                                                                      className="ant-btn-xs h-auto rounded-pill m-0 p-0 m-0 p-0"
                                                                      onClick={() => setActiveKey('2')}
                                                                  >
                                                                      <EditOutlined />
                                                                      Change
                                                                  </Button>
                                                              </>,
                                                          ]
                                                        : null
                                                }
                                            >
                                                <Form
                                                    className={'billing-information-form'}
                                                    layout="vertical"
                                                    form={billingInfoForm}
                                                    scrollToFirstError={true}
                                                    onFinish={onClickSaveBillingInfoHandler}
                                                    // onFieldsChange={handleBillingInfoFormChange}
                                                >
                                                    <Row gutter={[8, 0]} className="m-0 mb-3">
                                                        <AddressFormSimple
                                                            form={billingInfoForm}
                                                            addressType="Billing"
                                                            // address={savedBillingAddress}
                                                        />

                                                        <Col span={24}>
                                                            <Button type="primary" size="small" block htmlType="submit">
                                                                Next: Shipping Information
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Form>
                                            </Panel>
                                            {activeKey !== '2' && billingInfoFormCompleted ? (
                                                <div className="container">
                                                    <Row>
                                                        <Col span={24}>
                                                            <div className="d-flex flex-column">
                                                                <span className="font-size-base">
                                                                    {orderData?.billing_name}
                                                                </span>
                                                                <span className="font-size-base">
                                                                    {`${orderData?.billing_street}`}
                                                                </span>
                                                                <span className="font-size-base">
                                                                    {`${orderData?.billing_city}, ${orderData?.billing_state}, ${orderData?.billing_zip_code}`}
                                                                </span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            ) : null}

                                            <Panel
                                                header={<span className="px-1">Shipping Information</span>}
                                                key="3"
                                                showArrow={false}
                                                style={{ borderTop: '1px solid #e0e0e0' }}
                                                extra={
                                                    activeKey !== '3' && shippingInfoFormCompleted
                                                        ? [
                                                              <>
                                                                  <Button
                                                                      type="link"
                                                                      className="ant-btn-xs h-auto rounded-pill m-0 p-0 m-0 p-0"
                                                                      onClick={() => setActiveKey('3')}
                                                                  >
                                                                      <EditOutlined />
                                                                      Change
                                                                  </Button>
                                                              </>,
                                                          ]
                                                        : null
                                                }
                                            >
                                                <Form
                                                    className={'shipping-information-form'}
                                                    layout="vertical"
                                                    form={shippingInfoForm}
                                                    scrollToFirstError={true}
                                                    onFinish={onClickSaveShippingInfoHandler}
                                                    // onFieldsChange={handleShippingInfoFormChange}
                                                >
                                                    <Row gutter={[8, 0]} className="m-0 mb-3">
                                                        <Col span={24} className="my-2">
                                                            <Checkbox
                                                                checked={shippingSameAsBilling}
                                                                onChange={e => {
                                                                    setShippingSameAsBilling(e.target.checked);
                                                                    if (e.target.checked) {
                                                                        shippingInfoForm.setFieldsValue({
                                                                            shipping_name: orderData?.billing_name,
                                                                            shipping_street: orderData?.billing_street,
                                                                            shipping_city: orderData?.billing_city,
                                                                            shipping_state: orderData?.billing_state,
                                                                            shipping_zip_code:
                                                                                orderData?.billing_zip_code,
                                                                            shipping_country:
                                                                                orderData?.billing_country,
                                                                        });
                                                                    }
                                                                }}
                                                            >
                                                                Use Billing Address
                                                            </Checkbox>
                                                        </Col>
                                                        <AddressFormSimple
                                                            // defaultAddressName={props.account_name}
                                                            form={shippingInfoForm}
                                                            addressType="Shipping"
                                                            // address={savedShippingAddress}
                                                        />

                                                        <Col span={24}>
                                                            <Button type="primary" size="small" block htmlType="submit">
                                                                Next: Payment Information
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Form>
                                            </Panel>
                                            {activeKey !== '3' && shippingInfoFormCompleted ? (
                                                <div className="container">
                                                    <Row>
                                                        <Col span={24}>
                                                            <div className="d-flex flex-column">
                                                                <span className="font-size-base">
                                                                    {orderData?.shipping_name}
                                                                </span>
                                                                <span className="font-size-base">
                                                                    {`${orderData?.shipping_street}`}
                                                                </span>
                                                                <span className="font-size-base">
                                                                    {`${orderData?.shipping_city}, ${orderData?.shipping_state}, ${orderData?.shipping_zip_code}`}
                                                                </span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            ) : null}
                                            <Panel
                                                header={
                                                    <div className="d-flex align-items-center justify-content-between pl-2">
                                                        <span>Payment Information</span>
                                                    </div>
                                                }
                                                extra={
                                                    <div className="mt-1">
                                                        <SecurityMessage />
                                                    </div>
                                                }
                                                key="4"
                                                style={{ borderTop: '1px solid #e0e0e0' }}
                                                showArrow={false}
                                            >
                                                <Form.Item
                                                    name="credit_card_token"
                                                    className="m-0"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Credit card information is required',
                                                        },
                                                    ]}
                                                    hasFeedback={
                                                        QuickTurnPartStore.creditCardErrors.length >= 1 ? true : false
                                                    }
                                                    validateStatus={
                                                        QuickTurnPartStore.creditCardErrors.length >= 1
                                                            ? 'error'
                                                            : 'success'
                                                    }
                                                    help={
                                                        QuickTurnPartStore.creditCardErrors.length >= 1 ? (
                                                            <Alert
                                                                message="Payment Declined"
                                                                description={
                                                                    <div className="flex flex-col">
                                                                        <ul
                                                                            style={{
                                                                                listStyleType: 'none',
                                                                                paddingLeft: 0,
                                                                                marginBottom: 0,
                                                                            }}
                                                                        >
                                                                            {QuickTurnPartStore.creditCardErrors}
                                                                        </ul>
                                                                    </div>
                                                                }
                                                                type="error"
                                                                showIcon
                                                                icon={<CreditCardOutlined />}
                                                                className="mb-3"
                                                            />
                                                        ) : null
                                                    }
                                                >
                                                    <div className="text-left">
                                                        <SquarePaymentForm
                                                            buttonRef={buttonReference}
                                                            app_id={SQUARE_APP_ID}
                                                            location_id={SQUARE_LOCATION_ID}
                                                            script_url={SQUARE_ENVIRONMENT_URL}
                                                            onPayment={async sourceId => {
                                                                if (sourceId) {
                                                                    await handleSubmit(sourceId);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </Form.Item>
                                            </Panel>
                                        </Collapse>
                                    </div>
                                </Card>
                            </Col>
                            <Col lg={12} span={24}>
                                <Form
                                    className={'sales-order-form'}
                                    layout="vertical"
                                    form={form}
                                    scrollToFirstError={true}
                                >
                                    <Card title="Order Summary" className="shadow-sm">
                                        {orderData?.line_items?.map((item, index) => (
                                            <div key={item.id}>
                                                <div className="d-flex flex-row">
                                                    <div className="mr-3">
                                                        <ThreeJSThumbnail
                                                            id={item.part.id}
                                                            size="100px"
                                                            edges={false}
                                                            background="#ffffff"
                                                        />
                                                    </div>
                                                    <div className="d-flex flex-sm-row flex-column justify-content-between align-items-start w-100 py-3">
                                                        <div className="d-flex flex-column">
                                                            <div className="d-flex flex-column">
                                                                <div className="d-flex flex-column">
                                                                    <span className="font-size-base font-weight-semibold">
                                                                        {item.part.name}
                                                                    </span>

                                                                    <span className="font-size-base text-dark">{`${item.part.material.type} ${item.part.material.alloy}`}</span>
                                                                </div>
                                                            </div>

                                                            <span className="font-size-base font-weight-semibold">
                                                                Qty:{' '}
                                                                <span className="font-weight-normal">
                                                                    {item.part.quantity}
                                                                </span>
                                                            </span>
                                                        </div>
                                                        <div className="d-flex flex-column justify-content-center align-items-end align-self-end ml-3 mb-xl-3">
                                                            <span>
                                                                <Price
                                                                    style={{
                                                                        paddingLeft: '0.5rem',
                                                                    }}
                                                                    value={item.unit_price}
                                                                />{' '}
                                                                /pc
                                                            </span>

                                                            <Price
                                                                style={{
                                                                    fontSize: '1.1rem',
                                                                    fontWeight: 500,
                                                                    paddingLeft: '0.5rem',
                                                                }}
                                                                value={item.total_price}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <Divider className="my-1" />
                                            </div>
                                        ))}

                                        <div className="d-flex flex-column my-3">
                                            <div className="d-flex flex-row justify-content-between align-items-baseline mb-3">
                                                <span className="font-size-base font-weight-semibold">
                                                    Estimated Lead Time:
                                                </span>
                                                <span className="font-size-base font-weight-semibold">
                                                    7 Business Days
                                                </span>
                                            </div>
                                            <span className="d-flex flex-row justify-content-between mb-1">
                                                <span className="font-weight-semibold">Subtotal:</span>
                                                <span>
                                                    <Price value={orderData?.subtotal} />
                                                </span>
                                            </span>
                                            {orderData?.coupon?.code ? (
                                                <span className="d-flex flex-row justify-content-between mb-1 text-success">
                                                    <span className="font-weight-semibold">Discount:</span>
                                                    <span>
                                                        -<Price value={Number(orderData?.discount)} />
                                                    </span>
                                                </span>
                                            ) : null}
                                            <span className="d-flex flex-row justify-content-between mb-1">
                                                <span className="font-weight-semibold">Shipping:</span>
                                                <span>
                                                    <Price value={orderData?.shipping} />
                                                </span>
                                            </span>
                                            <span className="d-flex flex-row justify-content-between mb-1">
                                                <span className="font-weight-semibold">Tax:</span>
                                                <span>
                                                    <Price value={orderData?.tax} />
                                                </span>
                                            </span>
                                            <span className="d-flex flex-row justify-content-between font-size-lg font-weight-semibold mt-2">
                                                <span>Total:</span>
                                                <span className="font-size-lg font-weight-semibold">
                                                    <Price value={orderData?.grand_total} />
                                                </span>
                                            </span>
                                        </div>

                                        <div className="terms-and-conditions mt-2">
                                            <Form.Item
                                                style={{
                                                    marginBottom: 0,
                                                }}
                                                valuePropName="checked"
                                                name="terms-and-conditions"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message:
                                                            'Please agree all terms and conditions before place the order',
                                                    },
                                                ]}
                                            >
                                                <Checkbox>
                                                    I accept the{' '}
                                                    <a
                                                        className="underline"
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                        href="https://parts-badger.com/terms-and-conditions/"
                                                    >
                                                        Terms & Conditions
                                                    </a>
                                                </Checkbox>
                                            </Form.Item>
                                        </div>

                                        <Form.Item noStyle dependencies={['terms-and-conditions']}>
                                            {({ getFieldValue }) => {
                                                return (
                                                    <>
                                                        <Button
                                                            id="quickturn-submit-order"
                                                            type="primary"
                                                            size="large"
                                                            block
                                                            htmlType="submit"
                                                            className="d-flex justify-content-center"
                                                            style={{ columnGap: '6px' }}
                                                            onClick={async () => {
                                                                if (buttonReference.current) {
                                                                    await buttonReference.current.click();
                                                                }
                                                            }}
                                                            disabled={
                                                                !shippingInfoFormCompleted ||
                                                                !billingInfoFormCompleted ||
                                                                activeKey !== '4' ||
                                                                !getFieldValue('terms-and-conditions')
                                                            }
                                                            loading={buttonLoading}
                                                        >
                                                            <span>Place Order:</span>
                                                            <span>
                                                                {orderData?.grand_total ? (
                                                                    <Price value={orderData?.grand_total} />
                                                                ) : (
                                                                    0
                                                                )}
                                                            </span>
                                                        </Button>
                                                    </>
                                                );
                                            }}
                                        </Form.Item>
                                    </Card>
                                </Form>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        </>
    );
};

const SecurityMessage = () => {
    return (
        <div className="d-flex align-items-center text-muted font-size-sm font-weight-normal">
            <FeatherIcon icon="lock" size="16" className="mr-1" />
            <span>Payments Are Secure</span>
        </div>
    );
};

export default observer(Checkout);
